import { useSnapshot } from 'valtio';
import { outcomeLabels, requestStatusLabels, utilitiesPresentLabels } from '~/common/dataRequestUtils';
import type { Activity, CivilGridFile, Outcome, RequestStatus, UtilityPresence } from '~/common/types';
import MessageFile from '~/components2/Messages/MessageFile';
import { downloadAndTrackUtilitiesFile } from '../../common/trackUtilitiesFileDownload';
import store from '../../store';

function getAgencyName(a: Activity) {
  let agency = '';
  const id = a.details?.agency?.id || a.details?.agency;
  if (id && store.agencies[id]) {
    const { agencyName, type } = store.agencies[id];
    agency = `${agencyName} (${type})`;
  }

  return agency;
}

const onSupportingFileDownload = (f: CivilGridFile) =>
  downloadAndTrackUtilitiesFile(f, 'supportingFile', store.project?.id);

const onRequestFileDownload = (f: CivilGridFile) => downloadAndTrackUtilitiesFile(f, 'dataRequest', store.project?.id);

interface Props {
  activity: Activity;
  compact?: boolean;
  includeAgencyName?: boolean;
}

const MapsRequested = ({ activity, compact, includeAgencyName = true }: Props) => {
  if (compact) {
    return <>Request initiated</>;
  } else {
    return <>requested maps{includeAgencyName && ` for ${getAgencyName(activity)}`}</>;
  }
};

const AsbuiltsRequested = ({ activity, compact, includeAgencyName = true }: Props) => {
  if (compact) {
    return <>Request initiated</>;
  } else {
    return (
      <>
        requested as-builts
        {includeAgencyName && ` for ${getAgencyName(activity)}`}
      </>
    );
  }
};

const SupportingFileAdded = ({ activity }: Props) => {
  return (
    <>
      uploaded a supporting file{' '}
      {activity.supportingFiles.map((f) => (
        <MessageFile key={`activity-file-${f.id}`} file={f} onDownload={onSupportingFileDownload} />
      ))}
    </>
  );
};

const SupportingFileDeleted = ({ activity }: Props) => {
  return (
    <>
      removed a supporting file{' '}
      {activity.supportingFiles.map((f) => (
        <strong key={f.id}>{f.name}</strong>
      ))}
    </>
  );
};

const RequestFileAdded = ({ activity, compact, includeAgencyName = true }: Props) => {
  if (compact === true) {
    return <>Added a file</>;
  } else {
    return (
      <>
        added a file {includeAgencyName && `for ${getAgencyName(activity)}`}
        {activity.dataRequestFiles.map((f) => (
          <MessageFile key={`activity-file-${f.id}`} file={f} onDownload={onRequestFileDownload} />
        ))}
      </>
    );
  }
};

const RequestFileDeleted = ({ activity, compact }: Props) => {
  if (compact === true) {
    return <>Removed a request file</>;
  } else {
    return (
      <>
        removed a file
        {activity.dataRequestFiles.map((f) => (
          <strong key={f.id}>{f.name}</strong>
        ))}
      </>
    );
  }
};

const Updated = ({ activity, compact, includeAgencyName = true }: Props) => {
  if (compact === true) {
    return <>Added an update</>;
  }

  const activityUpdates = activity.details?.updates;
  if (!activityUpdates || activityUpdates.length === 0) {
    return <>{includeAgencyName ? ` updated ${getAgencyName(activity)}` : ' added an update'}</>;
  }

  const updates = activityUpdates.map((u: any) => {
    if (u.key === 'requestStatus') {
      return (
        <div key={u.key}>
          <span className="font-medium">Status:</span> {requestStatusLabels[u.value as RequestStatus]}
        </div>
      );
    } else if (u.key === 'utilityPresence') {
      return (
        <div key={u.key}>
          <span className="font-medium">Utility presence:</span> {utilitiesPresentLabels[u.value as UtilityPresence]}
        </div>
      );
    } else if (u.key === 'outcome') {
      return (
        <div key={u.key}>
          <span className="font-medium">Outcome:</span> {outcomeLabels[u.value as Outcome]}
        </div>
      );
    } else if (u.key === 'notes') {
      return (
        <div key={u.key}>
          <span className="font-medium">Note:</span> {u.value}
        </div>
      );
    }
    return null;
  });

  return (
    <>
      {includeAgencyName ? ` updated ${getAgencyName(activity)}` : ' added an update'}
      <br />
      <div className="mt-2 flex flex-col space-y-1">{updates}</div>
    </>
  );
};

const ProjectCreated = ({ activity }: Props) => {
  const { project } = useSnapshot(store);
  return (
    <>
      created the project <strong>{project?.name && project.name}</strong>
    </>
  );
};

const BoundaryUpdate = ({ activity }: Props) => {
  return <>updated the project boundary</>;
};

const UpdateAdded = ({ activity }: Props) => {
  return <>added a request update</>;
};

const UpdateDeleted = ({ activity }: Props) => {
  return <>removed a request update</>;
};

export {
  MapsRequested,
  AsbuiltsRequested,
  SupportingFileAdded,
  SupportingFileDeleted,
  RequestFileAdded,
  RequestFileDeleted,
  Updated,
  ProjectCreated,
  BoundaryUpdate,
  UpdateAdded,
  UpdateDeleted,
};
